import { Expose, Transform, Type } from 'class-transformer';
import { Model } from './model';
import { BaseTeacher } from './base-teacher-model';
import { TeacherUpdateInfoChanges } from 'src/app/shared/model';
import { getCookie } from 'src/app/shared/helper';
import { Constants, TeacherRequestStatus } from '../../common';

export class TeacherUpdateInfoRequestModel extends Model {
  @Expose() id: string;
  @Expose() brief: string;
  @Expose() briefEn: string;
  @Expose() briefEnStatus: string;
  @Expose() avatarUrl: string;
  @Expose() avatarUrlStatus: string;
  @Expose() briefStatus: string;
  @Expose() certificate: string;
  @Expose() certificateStatus: string;
  @Expose() ejaza: string;
  @Expose() ejazaStatus: string;
  @Expose() nameArStatus: string;
  @Expose() nameEnStatus: string;
  @Expose() nameAr: string;
  @Expose() nameEn: string;
  @Expose() categorise: string[];
  @Expose() categoriseStatus: string;
  @Expose() email: string;
  @Expose() emailStatus: string;
  @Expose() yearsOfExperience: string;
  @Expose() yearsOfExperienceStatus: string;
  @Expose() sessionPrice: string;
  @Expose() sessionPriceStatus: string;
  @Expose() sessionLongPrice: string;
  @Expose() sessionLongPriceStatus: string;

  @Transform(({ value }) => (value ? value.split(',&,').join(',') : null))
  @Expose()
  languages: string;
  @Expose()
  languagesStatus: string;
  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null))
  updatedAt: Date | null;

  @Expose()
  @Type(() => BaseTeacher)
  teacher: BaseTeacher;

  get changes(): TeacherUpdateInfoChanges[] {
    const changes: TeacherUpdateInfoChanges[] = [];
    const isArabic = getCookie(Constants.APP_LANGUAGE_COOKIE_KEY) == 'ar';
    for (const key in this) {
      if (this[key]) {
        if (key == 'brief' && this.briefStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.brief,
            changedPropertyOldValue: this.teacher?.brief,
            propertyLabel: isArabic ? 'النبذة المختصرة' : 'Brief',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'briefEn' && this.briefEnStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.briefEn,
            changedPropertyOldValue: this.teacher?.briefEn,
            propertyLabel: isArabic ? 'النبذة المختصرة بالانجليزية' : 'Brief In English',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'certificate' && this.certificateStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.certificate,
            changedPropertyOldValue: this.teacher?.certificate,
            propertyLabel: isArabic ? 'الشهادة الجامعية' : 'Certificate',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'email' && this.emailStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.email,
            changedPropertyOldValue: this.teacher?.email,
            propertyLabel: isArabic ? 'البريد الالكتروني' : 'Email Address',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'yearsOfExperience' && this.yearsOfExperienceStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.yearsOfExperience,
            changedPropertyOldValue: this.teacher?.yearsOfExperience,
            propertyLabel: isArabic ? 'سنوات الخبرة' : 'Years of Experience',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'avatarUrl' && this.avatarUrlStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.avatarUrl,
            changedPropertyOldValue: this.teacher?.avatarUrl,
            propertyLabel: isArabic ? 'الصورة الشخصية' : 'Profile Picture',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'languages' && this.languagesStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.languages,
            changedPropertyOldValue: this.teacher?.languages,
            propertyLabel: isArabic ? 'اللغات ' : 'Languages',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'ejaza' && this.ejazaStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.ejaza,
            changedPropertyOldValue: this.teacher?.ejaza.join(',&,'),
            propertyLabel: isArabic ? 'الاجازات ' : 'Ejazat',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'nameAr' && this.nameArStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.nameAr,
            changedPropertyOldValue: this.teacher?.user?.nameTrans?.ar,
            propertyLabel: isArabic ? 'الاسم بالعربية' : 'Arabic Name',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'nameEn' && this.nameEnStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.nameEn,
            changedPropertyOldValue: this.teacher?.user?.nameTrans?.en,
            propertyLabel: isArabic ? 'الاسم بالانجليزية' : 'ُEnglish Name',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key === 'categorise' && this.categoriseStatus === 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.categorise.join(isArabic ? '، ' : ', '),
            changedPropertyOldValue: this.teacher?.categorize
              .map((c) => c.dropDownDisplayName)
              .join(isArabic ? '، ' : ', '),
            propertyLabel: isArabic ? 'التصنيفات' : 'Categories',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'sessionPrice' && this.sessionPriceStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.sessionPrice,
            changedPropertyOldValue: this.teacher?.sessionPrice,
            propertyLabel: isArabic ? 'سعر الجلسة' : 'Session Price',
            status: TeacherRequestStatus.PENDING,
          });
        } else if (key == 'sessionLongPrice' && this.sessionLongPriceStatus == 'pending') {
          changes.push({
            requestId: this.id,
            changedPropertyName: key,
            changedPropertyNewValue: this.sessionLongPrice,
            changedPropertyOldValue: this.teacher?.sessionLongPrice,
            propertyLabel: isArabic ? 'سعر الجلسة الطويلة' : 'Session Long Price',
            status: TeacherRequestStatus.PENDING,
          });
        }
      }
    }
    return changes;
  }
}
